import { useAppForm } from "@/components/app-form";
import {
  BackButton,
  Footer,
  Header,
  Page,
  PageContent,
} from "@/components/page";
import { LoadingSpinner } from "@/components/spinner";
import { useShop } from "@/hooks/use-shop";
import { getToken } from "@/hooks/use-token";
import { useUser } from "@/hooks/use-user";
import { useApiErrorHandler } from "@/lib/api";
import { getServerUrl } from "@/lib/runtime-config";
import { revalidateLogic } from "@tanstack/react-form";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { Trans, useTranslation } from "react-i18next";
import type { TFunction } from "i18next";
import { z } from "zod";
import { useCountryCode } from "@frontend-common/country-provider";
import {
  localCountryNames,
  toMissingProfileDataInput,
  useMissingProfileDataStatus,
  useSubmitMissingProfileData,
} from "@frontend-common/hooks/use-missing-profile-data";
import { zipDigitsForCountry } from "@common/logic/countries";
import type { Country } from "@common/logic/services/country-service";
import { CountryPickerField } from "@/components/country-picker-field";
import { CouponSuccessView } from "@/components/missing-profile-data-modal";
import { useMuiModal } from "@/components/modal";
import { featureFlags } from "@common/feature-flags";
import { useAudioSlot } from "@/features/audio/kiosk-audio";

export const Route = createFileRoute("/profile-edit-screen")({
  component: RouteComponent,
});

const makeFormSchema = (t: TFunction, zipDigits: number) =>
  z
    .object({
      email: z.string(),
      firstName: z.string().nullish(),
      lastName: z.string().nullish(),
      birthyear: z.coerce.string().nullish(),
      phoneNumber: z
        .string()
        .min(8)
        .max(15)
        .startsWith("+")
        .regex(/^\+[0-9]+$/),
      residenceType: z.enum(["local", "foreign"]),
      zip: z.coerce.string(),
      residence: z.coerce.string(),
    })
    .superRefine((value, ctx) => {
      if (
        value.residenceType === "local" &&
        value.zip.trim() &&
        !new RegExp(`^[0-9]{${zipDigits}}$`).test(value.zip.trim())
      ) {
        ctx.addIssue({
          code: "custom",
          path: ["zip"],
          message: t("Zip code must be {{digits}} digits", {
            digits: zipDigits,
          }),
        });
      }

      if (value.residenceType === "foreign") {
        const residence = value.residence.trim();
        if (residence && (residence.length < 2 || residence.length > 80)) {
          ctx.addIssue({
            code: "custom",
            path: ["residence"],
            message: t("Enter a valid country"),
          });
        }
      }
    });

function RouteComponent() {
  useAudioSlot("page.profile-edit");
  const { data: shop } = useShop();
  const { data: user } = useUser();
  const errorHandler = useApiErrorHandler();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const countryCode = useCountryCode();
  const zipDigits = zipDigitsForCountry(countryCode);
  const countryName = t(localCountryNames[countryCode as Country]);
  const formSchema = makeFormSchema(t, zipDigits);

  const queryClient = useQueryClient();
  const { showModal, closeModal } = useMuiModal();
  const submitMissingProfileData = useSubmitMissingProfileData();
  const missingProfileDataStatus = useMissingProfileDataStatus();
  const editUserMutation = useMutation({
    mutationFn: async (user: { email: string; phoneNumber: string }) => {
      const response = await fetch(`${getServerUrl()}/users`, {
        method: "PATCH",
        body: JSON.stringify({
          email: user.email,
          phoneNumber: user.phoneNumber,
        }),
        headers: {
          "Kiosk-Key": shop.kioskKey,
          Authorization: "Bearer " + getToken(),
          "Content-Type": "application/json",
        },
      });

      const data = await errorHandler(response, undefined);
      await queryClient.invalidateQueries({ queryKey: ["user"] });
      return data;
    },
  });

  const form = useAppForm({
    defaultValues: {
      firstName: user?.firstName,
      lastName: user?.lastName,
      birthyear: user?.birthdate?.getFullYear(),
      email: user?.email,
      phoneNumber: user?.phoneNumber,
      residenceType: user?.residence ? "foreign" : "local",
      zip: user?.zip ?? "",
      residence: user?.residence ?? "",
    } as z.input<typeof formSchema>,
    validationLogic: revalidateLogic(),
    validators: {
      onDynamic: formSchema,
    },
    onSubmit: async ({ value }) => {
      const parsed = formSchema.parse(value);

      // Residence and phone also go through the missing-profile-data endpoint,
      // which grants the completion coupon when this save makes the profile
      // fully complete. It runs before the patch so the endpoint sees the
      // incomplete-to-complete transition.
      const missingDataInput = toMissingProfileDataInput(
        {
          residenceType: parsed.residenceType,
          zip: parsed.zip,
          country: parsed.residence,
          phoneNumber: parsed.phoneNumber,
        },
        zipDigits,
      );
      const residenceChanged =
        missingDataInput?.isLocal === true
          ? missingDataInput.zip !== (user?.zip ?? "")
          : missingDataInput?.isLocal === false
            ? missingDataInput.country !== (user?.residence ?? "")
            : false;
      const wasIncomplete = Object.values(
        missingProfileDataStatus.data?.missing ?? {},
      ).some(Boolean);

      try {
        const residenceResult =
          missingDataInput && (residenceChanged || wasIncomplete)
            ? await submitMissingProfileData.mutateAsync(missingDataInput)
            : undefined;

        const data = await editUserMutation.mutateAsync({
          email: parsed.email,
          phoneNumber: parsed.phoneNumber,
        });
        if (data === null) {
          return;
        }

        if (residenceResult?.couponCode) {
          await showModal(
            <CouponSuccessView
              code={residenceResult.couponCode}
              onClose={() => closeModal()}
            />,
          );
        }

        navigate({
          to: "/machine-type-selector-screen",
        });
      } catch {
        // The error handler already surfaced the failure; stay on the form.
      }
    },
  });

  if (!user) {
    return <LoadingSpinner />;
  }

  return (
    <Page>
      <Header
        showUserDetails
        backArrowHref={
          <BackButton
            label={t("Vissza")}
            onClick={() => {
              navigate({
                to: "/machine-type-selector-screen",
              });
            }}
          />
        }
      />
      <PageContent>
        {editUserMutation.isPending && <LoadingSpinner />}
        <div>
          <h2 className={`font-helvetica font-bold text-6xl text-center mb-8`}>
            <Trans>Adataim</Trans>
          </h2>
          <span>
            <Trans>*Kötelezően kitöltendő mező</Trans>
          </span>
          <form.AppForm>
            <div className="grid grid-cols-[1fr_1fr_1fr] gap-4">
              <form.AppField
                name="firstName"
                children={(f) => (
                  <f.TextField
                    tabIndex={1}
                    type="text"
                    label={t("Keresztnév")}
                    disabled
                  />
                )}
              />
              <form.AppField
                name="lastName"
                children={(f) => (
                  <f.TextField
                    tabIndex={2}
                    type="text"
                    label={t("Vezetéknév")}
                    disabled
                  />
                )}
              />
              <form.AppField
                name="birthyear"
                children={(f) => (
                  <f.TextField
                    tabIndex={3}
                    type="text"
                    label={t("Születési év")}
                    disabled
                  />
                )}
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <form.AppField
                name="phoneNumber"
                children={(f) => (
                  <f.PhoneField tabIndex={4} label={t("Telefonszám")} />
                )}
              />
              <form.AppField
                name="email"
                children={(f) => (
                  <f.TextField
                    tabIndex={5}
                    type="text"
                    label={t("E-mail cím")}
                    disabled
                  />
                )}
              />
            </div>

            {featureFlags.missingProfileDataModal && (
              <div className="mt-6 space-y-4">
                <form.AppField
                  name="residenceType"
                  children={(field) => (
                    <field.RadioGroupField
                      variant="buttons"
                      label={t("Do you live in {{countryName}}?", {
                        countryName,
                      })}
                      options={[
                        { value: "local", label: t("Yes") },
                        { value: "foreign", label: t("No") },
                      ]}
                    />
                  )}
                />

                <form.Subscribe
                  selector={(state) => state.values.residenceType}
                >
                  {(residenceType) =>
                    residenceType === "foreign" ? (
                      <form.AppField
                        name="residence"
                        children={(field) => (
                          <CountryPickerField
                            label={t("Country")}
                            value={field.state.value ?? ""}
                            onChange={(val) => field.setValue(val)}
                            onBlur={field.handleBlur}
                            placeholder={t("Select a country")}
                            className="max-w-md"
                            excludeCountryCode={countryCode}
                          />
                        )}
                      />
                    ) : (
                      <form.AppField
                        name="zip"
                        children={(field) => (
                          <field.TextField
                            tabIndex={6}
                            type="text"
                            inputMode="numeric"
                            maxLength={zipDigits}
                            label={t("Zip code")}
                            placeholder={t("Zip code")}
                            className="max-w-xs"
                          />
                        )}
                      />
                    )
                  }
                </form.Subscribe>
              </div>
            )}

            <div className="w-full flex justify-center mt-8">
              <form.SubmitButton>
                <Trans>Mentés</Trans>
              </form.SubmitButton>
            </div>
          </form.AppForm>
        </div>
      </PageContent>
      <Footer
        helpText={
          <Trans>
            Itt találod profilod adatait. Az itt található mezőkre kattintva
            módosíthatod adataidat.
          </Trans>
        }
      />
    </Page>
  );
}
